import React, { useRef, useState, useEffect, useContext } from 'react';
import { Box, Typography, Button, TextField, List, ListItem, ListItemText, IconButton, Paper, Divider, Avatar, CircularProgress, Alert, Chip } from '@mui/material';
import { 
  Delete, 
  UploadFile, 
  Person, 
  Report, 
  Note, 
  Chat,
  Visibility,
  Add,
  Send
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { collection, addDoc, deleteDoc, onSnapshot, doc, setDoc, getDocs } from 'firebase/firestore';
import { firestore } from '../config/firebase';
import { uploadFileAndGetURL } from '../utils/documentService';
import { AuthContext } from '../contexts/AuthContext';

interface Persona {
  id: string;
  name: string;
  fileUrl: string;
  fileType: string;
  userName?: string;
}

interface PainPoint {
  text: string;
  userName?: string;
}
interface ResearchNote {
  text: string;
  userName?: string;
}
interface CommentItem {
  text: string;
  userName?: string;
}

const UserResearchPhase: React.FC = () => {
  const { teamId, sprintId } = useParams<{ teamId: string; sprintId: string }>();
  const authContext = useContext(AuthContext);
  const currentUser = authContext?.user;

  // Personas
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [personaName, setPersonaName] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [personaLoading, setPersonaLoading] = useState(false);
  const [personaError, setPersonaError] = useState<string | null>(null);

  // Pain Points
  const [painPoints, setPainPoints] = useState<PainPoint[]>([]);
  const [newPainPoint, setNewPainPoint] = useState('');
  const [painLoading, setPainLoading] = useState(false);
  const [painError, setPainError] = useState<string | null>(null);

  // Research Notes
  const [researchNotes, setResearchNotes] = useState<ResearchNote[]>([]);
  const [newResearchNote, setNewResearchNote] = useState('');
  const [noteLoading, setNoteLoading] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);

  // Comments
  const [comments, setComments] = useState<CommentItem[]>([]);
  const [newComment, setNewComment] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);

  // Firestore listeners
  useEffect(() => {
    if (!teamId || !sprintId) return;
    // Personas
    const unsubPersonas = onSnapshot(collection(firestore, `teams/${teamId}/designSprints/${sprintId}/userResearch_personas`), snap => {
      setPersonas(snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Persona)));
    });
    // Pain Points
    const unsubPain = onSnapshot(collection(firestore, `teams/${teamId}/designSprints/${sprintId}/userResearch_painPoints`), snap => {
      setPainPoints(snap.docs.map(doc => doc.data() as any));
    });
    // Research Notes
    const unsubNotes = onSnapshot(collection(firestore, `teams/${teamId}/designSprints/${sprintId}/userResearch_notes`), snap => {
      setResearchNotes(snap.docs.map(doc => doc.data() as any));
    });
    // Comments
    const unsubComments = onSnapshot(collection(firestore, `teams/${teamId}/designSprints/${sprintId}/userResearch_comments`), snap => {
      setComments(snap.docs.map(doc => doc.data() as any));
    });
    return () => {
      unsubPersonas();
      unsubPain();
      unsubNotes();
      unsubComments();
    };
  }, [teamId, sprintId]);

  // Handle persona upload
  const handlePersonaUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && personaName && teamId && sprintId) {
      setPersonaLoading(true);
      setPersonaError(null);
      try {
        const filePath = `teams/${teamId}/designSprints/${sprintId}/personas/${Date.now()}_${file.name}`;
        const fileUrl = await uploadFileAndGetURL(file, filePath);
        await addDoc(collection(firestore, `teams/${teamId}/designSprints/${sprintId}/userResearch_personas`), {
          name: personaName,
          fileUrl,
          fileType: file.type,
          userName: currentUser?.displayName || currentUser?.email || 'Unknown',
        });
        setPersonaName('');
        if (fileInputRef.current) fileInputRef.current.value = '';
      } catch (err) {
        setPersonaError('Failed to upload persona.');
      }
      setPersonaLoading(false);
    }
  };

  const handleDeletePersona = async (id: string) => {
    if (!teamId || !sprintId) return;
    try {
      await deleteDoc(doc(firestore, `teams/${teamId}/designSprints/${sprintId}/userResearch_personas`, id));
    } catch (err) {
      setPersonaError('Failed to delete persona.');
    }
  };

  const handleAddPainPoint = async () => {
    if (!newPainPoint.trim() || !teamId || !sprintId) return;
    setPainLoading(true);
    setPainError(null);
    try {
      await addDoc(collection(firestore, `teams/${teamId}/designSprints/${sprintId}/userResearch_painPoints`), {
        text: newPainPoint.trim(),
        userName: currentUser?.displayName || currentUser?.email || 'Unknown',
      });
      setNewPainPoint('');
    } catch (err) {
      setPainError('Failed to add pain point.');
    }
      setPainLoading(false);
  };

  const handleDeletePainPoint = async (idx: number) => {
    if (!teamId || !sprintId) return;
    try {
      const querySnapshot = await getDocs(collection(firestore, `teams/${teamId}/designSprints/${sprintId}/userResearch_painPoints`));
      const docs = querySnapshot.docs;
      if (docs[idx]) {
        await deleteDoc(docs[idx].ref);
      }
    } catch (err) {
      setPainError('Failed to delete pain point.');
    }
  };

  const handleAddResearchNote = async () => {
    if (!newResearchNote.trim() || !teamId || !sprintId) return;
    setNoteLoading(true);
    setNoteError(null);
    try {
      await addDoc(collection(firestore, `teams/${teamId}/designSprints/${sprintId}/userResearch_notes`), {
        text: newResearchNote.trim(),
        userName: currentUser?.displayName || currentUser?.email || 'Unknown',
      });
      setNewResearchNote('');
    } catch (err) {
      setNoteError('Failed to add research note.');
    }
    setNoteLoading(false);
  };

  const handleDeleteResearchNote = async (idx: number) => {
    if (!teamId || !sprintId) return;
    try {
      const querySnapshot = await getDocs(collection(firestore, `teams/${teamId}/designSprints/${sprintId}/userResearch_notes`));
      const docs = querySnapshot.docs;
      if (docs[idx]) {
        await deleteDoc(docs[idx].ref);
      }
    } catch (err) {
      setNoteError('Failed to delete research note.');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !teamId || !sprintId) return;
    setCommentLoading(true);
    setCommentError(null);
    try {
      await addDoc(collection(firestore, `teams/${teamId}/designSprints/${sprintId}/userResearch_comments`), {
        text: newComment.trim(),
        userName: currentUser?.displayName || currentUser?.email || 'Unknown',
      });
      setNewComment('');
    } catch (err) {
      setCommentError('Failed to add comment.');
    }
    setCommentLoading(false);
  };

  return (
    <div className="space-y-6">
      {/* Personas Section */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center">
            <Person className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900 font-['Poppins']">Personas</h3>
            <p className="text-gray-600 text-sm font-['Inter']">Upload and manage user personas</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <TextField
            label="Persona Name"
            value={personaName}
            onChange={e => setPersonaName(e.target.value)}
            size="small"
            className="flex-1"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                fontFamily: 'Inter, sans-serif',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#3b82f6'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#3b82f6',
                  borderWidth: '2px'
                }
              }
            }}
            placeholder="Enter persona name..."
          />
          <Button
            variant="contained"
            component="label"
            startIcon={<UploadFile />}
            disabled={!personaName || personaLoading}
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              borderRadius: '12px',
              px: 3,
              py: 1.5,
              color: '#ffffff !important',
              background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%) !important',
              '&:hover': {
                background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%) !important',
                color: '#ffffff !important'
              }
            }}
          >
            Upload
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,application/pdf"
              hidden
              onChange={handlePersonaUpload}
            />
          </Button>
        </div>

        {personaLoading && (
          <div className="flex items-center gap-2 text-blue-600">
            <CircularProgress size={16} />
            <span className="text-sm font-['Inter']">Uploading persona...</span>
          </div>
        )}
        
        {personaError && (
          <Alert severity="error" sx={{ borderRadius: '12px', fontFamily: 'Inter, sans-serif' }}>
            {personaError}
          </Alert>
        )}

        <div className="space-y-3">
          {personas.map(persona => (
            <div key={persona.id} className="bg-white rounded-xl p-4 border border-gray-200 hover:shadow-md transition-all duration-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {persona.fileType.startsWith('image') ? (
                    <Avatar src={persona.fileUrl} alt={persona.name} sx={{ width: 48, height: 48 }} />
                  ) : (
                    <Avatar sx={{ width: 48, height: 48, bgcolor: 'blue.500' }}>
                      <UploadFile />
                    </Avatar>
                  )}
                  <div>
                    <h4 className="font-semibold text-gray-900 font-['Poppins']">{persona.name}</h4>
                    <p className="text-sm text-gray-500 font-['Inter']">by {persona.userName || 'Unknown'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Visibility />}
                    href={persona.fileUrl}
                    target="_blank"
                    sx={{
                      borderRadius: '8px',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '0.75rem'
                    }}
                  >
                    View
                  </Button>
                  <IconButton
                    size="small"
                    onClick={() => handleDeletePersona(persona.id)}
                    disabled={personaLoading}
                    sx={{
                      color: '#ef4444',
                      '&:hover': { backgroundColor: '#fef2f2' }
                    }}
                  >
                    <Delete />
                  </IconButton>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Pain Points Section */}
      <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-2xl p-6 border border-red-100">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-red-500 rounded-xl flex items-center justify-center">
            <Report className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900 font-['Poppins']">Pain Points</h3>
            <p className="text-gray-600 text-sm font-['Inter']">Identify and document user pain points</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <TextField
            label="Add Pain Point"
            value={newPainPoint}
            onChange={e => setNewPainPoint(e.target.value)}
            size="small"
            fullWidth
            disabled={painLoading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                fontFamily: 'Inter, sans-serif',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#ef4444'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#ef4444',
                  borderWidth: '2px'
                }
              }
            }}
            placeholder="Describe a user pain point..."
            onKeyPress={(e) => e.key === 'Enter' && handleAddPainPoint()}
          />
          <Button
            variant="contained"
            onClick={handleAddPainPoint}
            disabled={painLoading || !newPainPoint.trim()}
            startIcon={<Add />}
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              borderRadius: '12px',
              px: 3,
              py: 1.5,
              color: '#ffffff !important',
              background: 'linear-gradient(135deg, #ef4444 0%, #ec4899 100%) !important',
              '&:hover': {
                background: 'linear-gradient(135deg, #dc2626 0%, #db2777 100%) !important',
                color: '#ffffff !important'
              }
            }}
          >
            Add
          </Button>
        </div>

        {painLoading && (
          <div className="flex items-center gap-2 text-red-600">
            <CircularProgress size={16} />
            <span className="text-sm font-['Inter']">Adding pain point...</span>
          </div>
        )}
        
        {painError && (
          <Alert severity="error" sx={{ borderRadius: '12px', fontFamily: 'Inter, sans-serif' }}>
            {painError}
          </Alert>
        )}

        <div className="space-y-3">
          {painPoints.map((point, idx) => (
            <div key={idx} className="bg-white rounded-xl p-4 border border-gray-200 hover:shadow-md transition-all duration-200">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-gray-900 font-['Inter']">{point.text}</p>
                  <p className="text-sm text-gray-500 font-['Inter'] mt-1">by {point.userName || 'Unknown'}</p>
                </div>
                <IconButton
                  size="small"
                  onClick={() => handleDeletePainPoint(idx)}
                  disabled={painLoading}
                  sx={{
                    color: '#ef4444',
                    '&:hover': { backgroundColor: '#fef2f2' }
                  }}
                >
                  <Delete />
                </IconButton>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Research Notes Section */}
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-100">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center">
            <Note className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900 font-['Poppins']">Research Notes</h3>
            <p className="text-gray-600 text-sm font-['Inter']">Document key research findings</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <TextField
            label="Add Research Note"
            value={newResearchNote}
            onChange={e => setNewResearchNote(e.target.value)}
            size="small"
            fullWidth
            disabled={noteLoading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                fontFamily: 'Inter, sans-serif',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#10b981'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#10b981',
                  borderWidth: '2px'
                }
              }
            }}
            placeholder="Add a research note..."
            onKeyPress={(e) => e.key === 'Enter' && handleAddResearchNote()}
          />
          <Button
            variant="contained"
            onClick={handleAddResearchNote}
            disabled={noteLoading || !newResearchNote.trim()}
            startIcon={<Add />}
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              borderRadius: '12px',
              px: 3,
              py: 1.5,
              color: '#ffffff !important',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%) !important',
              '&:hover': {
                background: 'linear-gradient(135deg, #059669 0%, #047857 100%) !important',
                color: '#ffffff !important'
              }
            }}
          >
            Add
          </Button>
        </div>

        {noteLoading && (
          <div className="flex items-center gap-2 text-green-600">
            <CircularProgress size={16} />
            <span className="text-sm font-['Inter']">Adding research note...</span>
          </div>
        )}
        
        {noteError && (
          <Alert severity="error" sx={{ borderRadius: '12px', fontFamily: 'Inter, sans-serif' }}>
            {noteError}
          </Alert>
        )}

        <div className="space-y-3">
          {researchNotes.map((note, idx) => (
            <div key={idx} className="bg-white rounded-xl p-4 border border-gray-200 hover:shadow-md transition-all duration-200">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-gray-900 font-['Inter']">{note.text}</p>
                  <p className="text-sm text-gray-500 font-['Inter'] mt-1">by {note.userName || 'Unknown'}</p>
                </div>
                <IconButton
                  size="small"
                  onClick={() => handleDeleteResearchNote(idx)}
                  disabled={noteLoading}
                  sx={{
                    color: '#ef4444',
                    '&:hover': { backgroundColor: '#fef2f2' }
                  }}
                >
                  <Delete />
                </IconButton>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Comments Section */}
      <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-100">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-purple-500 rounded-xl flex items-center justify-center">
            <Chat className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900 font-['Poppins']">Team Discussion</h3>
            <p className="text-gray-600 text-sm font-['Inter']">Share thoughts and collaborate</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <TextField
            label="Write a comment..."
            value={newComment}
            onChange={e => setNewComment(e.target.value)}
            size="small"
            fullWidth
            disabled={commentLoading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                fontFamily: 'Inter, sans-serif',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#8b5cf6'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#8b5cf6',
                  borderWidth: '2px'
                }
              }
            }}
            placeholder="Share your thoughts..."
            onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
          />
          <Button
            variant="contained"
            onClick={handleAddComment}
            disabled={commentLoading || !newComment.trim()}
            startIcon={<Send />}
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              borderRadius: '12px',
              px: 3,
              py: 1.5,
              color: '#ffffff !important',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%) !important',
              '&:hover': {
                background: 'linear-gradient(135deg, #7c3aed 0%, #db2777 100%) !important',
                color: '#ffffff !important'
              }
            }}
          >
            Send
          </Button>
        </div>

        {commentLoading && (
          <div className="flex items-center gap-2 text-purple-600">
            <CircularProgress size={16} />
            <span className="text-sm font-['Inter']">Sending comment...</span>
          </div>
        )}
        
        {commentError && (
          <Alert severity="error" sx={{ borderRadius: '12px', fontFamily: 'Inter, sans-serif' }}>
            {commentError}
          </Alert>
        )}

        <div className="space-y-3">
          {comments.map((comment, idx) => (
            <div key={idx} className="bg-white rounded-xl p-4 border border-gray-200">
              <div className="flex items-start gap-3">
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'purple.500' }}>
                  {(comment.userName || 'U').charAt(0).toUpperCase()}
                </Avatar>
                <div className="flex-1">
                  <p className="text-gray-900 font-['Inter']">{comment.text}</p>
                  <p className="text-sm text-gray-500 font-['Inter'] mt-1">by {comment.userName || 'Unknown'}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default UserResearchPhase; 